<template>
	<BaseLayout
		:footer-full="true"
		:include-padding="false"
		:footnotes="displayData.footnotes"
		:transparent-header="true"
		background-color-class="bg-color-content-primary"
	>
		<div class="marketing-site-how-it-works">
			<MarketingHeroImage :hero="displayData.hero" theme="dark" :has-bottom-padding="false">
				<template #cta>
					<div
						:class="[
							'display-flex flex-direction-column mt-200-md mt-150-smo width-100-smo text-align-left',
							{ 'flex-direction-column-reverse gap-100': thirdPartyAuthStore.isSignupEnabled }
						]"
					>
						<LeadCaptureComponent :form-config="leadCaptureConfig" />
						<p
							class="body-sm text-color-gray-20"
							v-html="deorphanText('Start investing in less than 5 minutes and with as little as $10.')"
						/>
					</div>
				</template>
			</MarketingHeroImage>
			<PartnerLogosMarquee theme="dark" />
			<HowItWorksOurPlatform />
			<HowItWorksOurAssets />
			<HowItWorksTechnology />
			<WaysToInvestSection />
			<div class="marketing-section bg-color-greige">
				<div class="container">
					<h2 class="heading-2 text-align-center mb-200">Important details</h2>
					<div class="faq-section col-10-md col-6-lg ml-auto mr-auto">
						<QuestionList :question-items="displayData.importantDetails" />
					</div>
				</div>
			</div>
			<CtaSection theme="dark" />
		</div>
	</BaseLayout>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import { currency, deorphanText } from '@filters/shared-filters';
import { SanityGlobalSocialStats, SanitySocialStat } from 'types/sanity/marketing-components';
import { ageLimits } from '@constants/age-limits';
import { appBasePath } from '@constants';
import CtaSection from '@components/marketing-site/cta-section.vue';
import { getSocialStats } from '@api/sanity/graphql/global-social-stats';
import { HowItWorksData } from 'types/marketing-site';
import HowItWorksOurAssets from '@components/marketing-site/how-it-works/how-it-works-our-assets.vue';
import HowItWorksOurPlatform from '@components/marketing-site/how-it-works/how-it-works-our-platform.vue';
import HowItWorksTechnology from '@components/marketing-site/how-it-works/how-it-works-technology.vue';
import LeadCaptureComponent from '@components/lead-capture/lead-capture-form.vue';
import { LeadCaptureFormConfig } from 'types/layout/lead';
import MarketingHeroImage from '@components/marketing-site/marketing-hero-image.vue';
import PartnerLogosMarquee from '@components/marketing-site/partner-logos-marquee.vue';
import QuestionList from '@components/layout/question-list.vue';
import { useAppStore } from '@stores/app';
import { useHead } from '@unhead/vue';
import { useThirdPartyAuthStore } from '@stores/third-party-auth';
import WaysToInvestSection from '@components/marketing-site/ways-to-invest-section.vue';

const appStore = useAppStore();
const thirdPartyAuthStore = useThirdPartyAuthStore();

const metaTitle = 'How it Works';
const metaDescription =
	'Fundrise makes real estate and venture investing simple and accessible by combining innovative technology with expert private equity management.';
const investorCount = ref('');
const leadCaptureConfig: LeadCaptureFormConfig = {
	buttonText: 'Sign up',
	signupFlowType: 'STANDARD',
	thirdPartyAuthTheme: 'SECONDARY'
};

const displayData: HowItWorksData = reactive({
	hero: {
		heading: 'How it works',
		headingSize: 'heading-1',
		title: 'Quality assets. Low&nbsp;fees. Smart&nbsp;technology.',
		description: `We blend our investment expertise with smart technology to provide our ${investorCount.value} investors with the buying power and investment opportunities traditionally reserved for billion dollar institutions.`,
		imageUrl: `/images/marketing-site/how-it-works/how-it-works-hero-${appStore.isMobile ? 'mobile' : 'desktop'}.png`,
		backgroundColor: '#151513'
	},
	importantDetails: [
		{
			question: 'Who can invest with Fundrise?',
			answer: `Any U.S. citizen (or permanent resident) currently residing in the U.S. who is over the age of ${ageLimits.investorAge.min}. No accreditation required.`
		},
		{
			question: 'How does pricing work?',
			answer:
				`Investors pay a 0.15% annual investment advisory fee, which may be waived under certain circumstances. This means that over a 12-month period, you will pay a ${currency(
					1.5,
					2
				)} advisory fee for every ${currency(
					1000,
					0
				)} you've invested with us. Separately, the funds in our standard portfolios pay a 0.85% annual asset management fee. That's ${currency(
					8.5,
					2
				)}/year for every ${currency(
					1000,
					0
				)} invested. You can find a full description of all fees in our <a class='text-color-current-color text-decoration-underline' href='` +
				appBasePath +
				"/oc?cta=Top%20Questions%20-%20Fees'>offering circulars</a>."
		},
		{
			question: 'Is Fundrise a long-term investment?',
			answer: "Absolutely. Like all private market investments, investments in Fundrise should be viewed as long-term (5+ years). This is because we specifically select strategies based on their long-term return potential for our investors, not short-term optics. If you anticipate needing your investment back in the near-term, we don't recommend investing with us."
		},
		{
			question: 'How does liquidity work at Fundrise?',
			answer: `
				<p>
				While Fundrise should be viewed as a long-term investment, we understand that most investors value optionality. That’s why one of Fundrise’s most popular features is our share repurchase program (otherwise known as our redemption program).
				</p>
				<p>
					Our core investments<sup>1</sup> offer:	
				</p>
				<ul class="ul ml-150">
					<li>Quarterly liquidity: You can redeem some or all of your shares, subject to limitations, at any point, and redemptions are processed at the beginning of each quarter. For example, if you requested to redeem shares in August, your redemption would expect to be processed at the start of October.</li>
					<li>NO redemption penalties: There is no penalty or cost associated with redemption.</li>
				</ul>
				<p>
					Thanks to our redemption program, you aren’t reliant on an unpredictable secondary market, and—most importantly—you don’t have to trade flexibility for the long-term benefits of private market investing.
				</p>	`
		},
		{
			question: 'How do returns work at Fundrise?',
			answer: `
					<p>Real estate famously offers investors a unique mix of long-term appreciation and cash flow potential. Private credit exclusively focuses on income generation. Venture capital, on the other hand, is known for its outsized long-term growth potential.</p>
					<p>Investing with Fundrise is no different. Depending on strategy, Fundrise investor portfolios have the potential to generate dividends on a quarterly basis while their shares also potentially grow in value over time. These returns ultimately come from the individual assets in your portfolio – loan interest or rental income collected, as well as potential appreciation in the value of each property and/or company.</p>
				`
		}
	],
	footnotes: [
		'“Core investments” refers solely to funds registered under the Investment Company Act of 1940, and does not refer to any funds offered under Regulation A or Regulation D of the Securities Act, which may contain redemption penalties.'
	]
});

const heroDescription = computed(() => {
	return `We blend our investment expertise with smart technology to provide our ${investorCount.value} investors with the buying power and investment opportunities traditionally reserved for billion dollar institutions.`;
});

async function setup(): Promise<void> {
	useHead({
		title: metaTitle,
		meta: [
			{ property: 'og:title', content: metaTitle },
			{ name: 'description', content: metaDescription },
			{ property: 'og:description', content: metaDescription }
		]
	});

	const socialStats: SanityGlobalSocialStats | null = await getSocialStats('how-it-works');
	const investorStats: SanitySocialStat | null =
		socialStats?.statistics.find((stat) => stat.slug.current === 'investors') ?? null;
	investorCount.value = investorStats?.value || '';
	displayData.hero.description = heroDescription.value;
}

setup();
</script>
